// Employee/components/View/EventCard.tsx
import React, {useCallback} from "react";
import {Box, Paper, Tooltip, Typography} from "@mui/material";
import {keyframes} from "@emotion/react";
import {styles} from "../../styles/WeekViewStyles";
import {Event, EventAddData} from "../../types";
import {addMinutes, differenceInMinutes, format, isWithinInterval, parseISO,} from "date-fns";
import moment, {Moment} from "moment";
import {useNavigate} from "react-router-dom";
import {useTheme} from "../../../Theme/ThemeContext";

interface EventCardProps {
    event: Event;
    style?: React.CSSProperties;
    onEdit: (event: Event) => void;
    onAdd: (eventData: EventAddData) => void;
    onRemove: (event: Event) => void;
    onSlotSelected?: (slot: Moment) => void;
    userType: "client" | "psychologist" | "observer";
    timeSlotStart: Date;
    timeSlotEnd: Date;
    eventStartInCell: Date;
    eventEndInCell: Date;
    removeGap: boolean;
    canEdit: boolean;
    showStartTime: boolean;
    showEndTime: boolean;
    isFirstCell: boolean;
    isLastCell: boolean;
    showTimeText: boolean;
}

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

export const EventCard: React.FC<EventCardProps> = ({
                                                        event,
                                                        style,
                                                        onEdit,
                                                        onAdd,
                                                        userType,
                                                        canEdit,
                                                        timeSlotStart,
                                                        timeSlotEnd,
                                                        removeGap,
                                                        showStartTime,
                                                        eventStartInCell,
                                                        showEndTime,
                                                        onSlotSelected,
                                                        eventEndInCell,
                                                        isFirstCell,
                                                        isLastCell,
                                                        showTimeText,
                                                    }) => {
    const {plt} = useTheme();
    const eventStart = parseISO(event.start);
    const eventEnd = parseISO(event.end);
    const eventDuration = differenceInMinutes(eventEnd, eventStart);
    const isOneHourOrLess = eventDuration <= 60;
    const displayStartTime = isFirstCell ? eventStart : eventStartInCell;
    const displayEndTime = isLastCell ? eventEnd : eventEndInCell;
    const navigate = useNavigate();
    const isEventStartInSlot = isWithinInterval(eventStart, {
        start: timeSlotStart,
        end: timeSlotEnd,
    });
    const isEventEndInSlot = isWithinInterval(eventEnd, {
        start: timeSlotStart,
        end: addMinutes(timeSlotEnd, 1),
    });
    const eventHeightPercentage = (() => {
        const totalMinutesInSlot = differenceInMinutes(timeSlotEnd, timeSlotStart);
        const eventMinutesInSlot = differenceInMinutes(
            eventEndInCell,
            eventStartInCell
        );
        return (eventMinutesInSlot / totalMinutesInSlot) * 100;
    })();

    const eventTopPercentage = (() => {
        const totalMinutesInSlot = differenceInMinutes(timeSlotEnd, timeSlotStart);
        const minutesFromSlotStart = differenceInMinutes(
            eventStartInCell,
            timeSlotStart
        );
        return (minutesFromSlotStart / totalMinutesInSlot) * 100;
    })();

    const showButton =
        userType !== "observer" &&
        ((userType === "psychologist" && canEdit) ||
            (userType === "client" && event.status === "AVAILABLE"));

    const handleSlotClick = useCallback(() => {
        if (event.status !== "AVAILABLE") {
            navigate(`/consultations/${event.id}/`);
            return;
        }

        if (userType === "psychologist" && event.status === "AVAILABLE") {
            onEdit(event);
        } else if (userType === "client" && event.status === "AVAILABLE") {
            const totalMinutesInSlot = differenceInMinutes(
                timeSlotEnd,
                timeSlotStart
            );
            const eventMinutesInSlot = differenceInMinutes(
                eventEndInCell,
                eventStartInCell
            );
            const remainingMinutes = totalMinutesInSlot - eventMinutesInSlot;

            // Only allow selection if there's at least 30 minutes available
            if (remainingMinutes >= 30) {
                const newEventStart =
                    timeSlotStart > eventStart ? timeSlotStart : eventStart;
                // Calculate the maximum possible duration while respecting remaining time
                const maxDuration = Math.min(60, remainingMinutes);
                const newEventEnd = addMinutes(newEventStart, maxDuration);

                if (
                    newEventEnd <= eventEnd &&
                    newEventEnd <= timeSlotEnd &&
                    onSlotSelected
                ) {
                    onSlotSelected(moment(newEventStart));
                }
            }
        }
    }, [
        showButton,
        userType,
        event,
        timeSlotStart,
        timeSlotEnd,
        eventStart,
        eventEnd,
        eventStartInCell,
        eventEndInCell,
        onSlotSelected,
        navigate,
    ]);

    // Add a function to generate tooltip content
    const getTooltipContent = () => {
        return (
            <Box
                sx={{
                    p: 1.5,
                    backgroundColor:
                        plt.mode === "dark" ? "#2a2a2a" : "#f5f5f5",
                    borderRadius: 1,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: "bold",
                        color: plt.mode === "dark" ? "white" : "black",
                        mb: 0.5,
                    }}
                >
                    {format(eventStart, "dd.MM.yyyy")}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: plt.mode === "dark" ? "#e0e0e0" : "#424242",
                        mb: 0.5,
                    }}
                >
                    {format(eventStart, "HH:mm")} - {format(eventEnd, "HH:mm")}
                </Typography>
                {event.title && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: plt.mode === "dark" ? "#e0e0e0" : "#424242",
                            mb: 0.5,
                        }}
                    >
                        {event.title}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    sx={{
                        color: plt.mode === "dark" ? "#9e9e9e" : "#666666",
                    }}
                >
                    Status: {event.status}
                </Typography>
            </Box>
        );
    };

    return (
        <Tooltip
            title={getTooltipContent()}
            arrow
            placement="top"
            enterDelay={200}
            leaveDelay={0}
            slotProps={{
                tooltip: {
                    sx: {
                        bgcolor: "transparent",
                        "& .MuiTooltip-arrow": {
                            color: plt.mode === "dark" ? "#2a2a2a" : "#f5f5f5",
                        },
                    },
                },
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    ...styles.eventCard,
                    backgroundColor: event.color || styles.eventCard.backgroundColor,
                    ...style,
                    height: `${eventHeightPercentage}%`,
                    top: `${eventTopPercentage}%`,
                    borderRadius: removeGap ? 0 : "4px",
                    borderTopLeftRadius: isEventStartInSlot ? "4px" : 0,
                    borderTopRightRadius: isEventStartInSlot ? "4px" : 0,
                    borderBottomLeftRadius: isOneHourOrLess
                        ? isEventEndInSlot
                            ? "4px"
                            : 0
                        : showEndTime
                            ? "4px"
                            : 0,
                    borderBottomRightRadius: isOneHourOrLess
                        ? isEventEndInSlot
                            ? "4px"
                            : 0
                        : showEndTime
                            ? "4px"
                            : 0,
                    cursor:
                        showButton || event.status !== "AVAILABLE" ? "pointer" : "default",
                    animation: `${fadeIn} 0.3s ease-in`,
                }}
                onClick={handleSlotClick}
                onTouchStart={handleSlotClick}
            >
                {showTimeText ? (
                    <Box sx={styles.eventTimeContainer}>
                        <Typography variant="body2" sx={styles.eventTime}>
                            {format(eventStart, "HH:mm")}-{format(eventEnd, "HH:mm")}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {showStartTime && (
                            <Box sx={styles.eventTimeContainer}>
                                <Typography variant="body2" sx={styles.eventTime}>
                                    {format(eventStart, "HH:mm")}
                                </Typography>
                            </Box>
                        )}
                        {showEndTime && (
                            <Box sx={styles.eventTimeContainer}>
                                <Typography variant="body2" sx={styles.eventTime}>
                                    {format(eventEnd, "HH:mm")}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        </Tooltip>
    );
};
